/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  CHART_HEIGHT,
  ChartEmpty,
  buildAdereMalhaDrilldownModel,
  buildAdereMonthlyOverviewModel,
  buildAdereStatusStackedSeries,
  deriveAdereStatusOrder,
} from './chartShared';
import { getChartColorForStatus, getStatusLabel, resolveChartStatusOrder } from './constants';

const ADERE_X_AXIS_ID = 'adere-x-axis';

const AdereByMalhaChart = ({
  items,
  statusOrder,
  loading,
  height,
  selectedAdereMonth,
  onAdereMonthChange,
  onSegmentSelect,
}) => {
  const theme = useTheme();
  const [selectedBar, setSelectedBar] = useState(null);
  const [hiddenStatuses, setHiddenStatuses] = useState(() => new Set());

  const isDrillDown = Boolean(selectedAdereMonth);

  useEffect(() => {
    setSelectedBar(null);
  }, [selectedAdereMonth]);

  const resolvedOrder = useMemo(() => {
    const fallback = resolveChartStatusOrder(statusOrder);
    return deriveAdereStatusOrder(items, fallback);
  }, [items, statusOrder]);

  const visibleStatusOrder = useMemo(
    () => resolvedOrder.filter((status) => !hiddenStatuses.has(status)),
    [resolvedOrder, hiddenStatuses],
  );

  const chartModel = useMemo(() => {
    if (isDrillDown) {
      return buildAdereMalhaDrilldownModel(items, selectedAdereMonth, resolvedOrder);
    }
    return buildAdereMonthlyOverviewModel(items, resolvedOrder);
  }, [items, resolvedOrder, selectedAdereMonth, isDrillDown]);

  const { dataset } = chartModel;

  const series = useMemo(
    () =>
      buildAdereStatusStackedSeries(
        visibleStatusOrder,
        theme,
        getChartColorForStatus,
        (status) => getStatusLabel(status),
      ).map((entry) => ({
        ...entry,
        valueFormatter: (value) => `${value ?? 0} NSIRs`,
      })),
    [visibleStatusOrder, theme],
  );

  const chartHeight = height || CHART_HEIGHT;
  const hasData = dataset.length > 0;
  const axisLabelColor = theme.palette.text.secondary;

  const xAxisConfig = useMemo(
    () => [
      {
        id: ADERE_X_AXIS_ID,
        scaleType: 'band',
        dataKey: isDrillDown ? 'malhaLabel' : 'monthLabel',
        label: isDrillDown ? 'Malha' : 'Mês Adere',
        labelStyle: {
          fill: axisLabelColor,
          fontSize: 11,
          fontWeight: 500,
        },
        tickLabelStyle: {
          fill: axisLabelColor,
          fontSize: 11,
        },
      },
    ],
    [axisLabelColor, isDrillDown],
  );

  const chartMargin = useMemo(
    () => ({
      left: 48,
      right: 16,
      top: 8,
      bottom: 52,
    }),
    [],
  );

  const toggleStatusVisibility = useCallback((status) => {
    setHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
        return next;
      }
      if (next.size >= resolvedOrder.length - 1) return prev;
      next.add(status);
      return next;
    });
  }, [resolvedOrder.length]);

  const handleItemClick = useCallback(
    (event, identifier) => {
      event.stopPropagation();

      if (!isDrillDown) {
        const row = dataset[identifier.dataIndex];
        if (row?.adereMonth && onAdereMonthChange) {
          onAdereMonthChange(row.adereMonth);
        }
        return;
      }

      const isSame =
        selectedBar?.seriesId === identifier.seriesId
        && selectedBar?.dataIndex === identifier.dataIndex;

      const next = isSame ? null : identifier;
      setSelectedBar(next);

      if (onSegmentSelect) {
        if (!next) {
          onSegmentSelect(null);
          return;
        }
        const row = dataset[next.dataIndex];
        onSegmentSelect({
          adereMonth: selectedAdereMonth,
          malhaId: row?.malhaId,
          malhaLabel: row?.malhaLabel,
          status: next.seriesId,
          dataIndex: next.dataIndex,
        });
      }
    },
    [
      dataset,
      isDrillDown,
      onAdereMonthChange,
      onSegmentSelect,
      selectedAdereMonth,
      selectedBar,
    ],
  );

  const highlightedItem = selectedBar
    ? { type: 'bar', seriesId: selectedBar.seriesId, dataIndex: selectedBar.dataIndex }
    : null;

  const highlightedAxis = selectedBar != null
    ? [{ axisId: ADERE_X_AXIS_ID, dataIndex: selectedBar.dataIndex }]
    : [];

  if (loading) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ width: '100%' }} />;
  }

  if (!hasData) {
    const message = isDrillDown
      ? 'Sem NSIRs com Adere neste mês'
      : 'Sem NSIRs com Adere no período selecionado';
    return <ChartEmpty message={message} />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', minHeight: chartHeight }}>
        <BarChart
          height={chartHeight}
          dataset={dataset}
          series={series}
          xAxis={xAxisConfig}
          yAxis={[{ min: 0, label: 'NSIRs', labelStyle: { fill: axisLabelColor, fontSize: 11 } }]}
          margin={chartMargin}
          hideLegend
          highlightedItem={highlightedItem}
          highlightedAxis={highlightedAxis}
          onItemClick={handleItemClick}
          slotProps={{
            tooltip: {
              trigger: 'item',
            },
          }}
          sx={{ width: '100%', maxWidth: '100%' }}
        />
      </Box>

      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={0.75}
        sx={{ mt: 1.5, justifyContent: 'center' }}
      >
        {resolvedOrder.map((status) => {
          const hidden = hiddenStatuses.has(status);
          const color = getChartColorForStatus(status, theme);
          return (
            <Chip
              key={status}
              size="small"
              label={getStatusLabel(status)}
              onClick={() => toggleStatusVisibility(status)}
              variant={hidden ? 'outlined' : 'filled'}
              sx={{
                opacity: hidden ? 0.45 : 1,
                bgcolor: hidden ? 'transparent' : `${color}22`,
                borderColor: color,
                color: hidden ? 'text.secondary' : color,
                fontWeight: 600,
                '& .MuiChip-label': { fontSize: '0.75rem' },
              }}
            />
          );
        })}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {isDrillDown
          ? 'NSIRs do mês selecionado por malha. Clique em um segmento para filtrar a tabela.'
          : 'Uma barra por mês Adere (empilhada por status). Clique em um mês para ver as malhas.'}
      </Typography>
    </Box>
  );
};

AdereByMalhaChart.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  statusOrder: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  height: PropTypes.number,
  selectedAdereMonth: PropTypes.string,
  onAdereMonthChange: PropTypes.func,
  onSegmentSelect: PropTypes.func,
};

AdereByMalhaChart.defaultProps = {
  items: [],
  statusOrder: null,
  loading: false,
  height: null,
  selectedAdereMonth: null,
  onAdereMonthChange: null,
  onSegmentSelect: null,
};

export default AdereByMalhaChart;
